/**
 * AdminInscricoes — gestão das Inscrições de Finalistas.
 *
 * Features:
 *   - Tabela: STK · Data · Escola · Email · Jogo · Credenciais · Total · Estado · Ações
 *   - Filtros: pesquisa, jogo, datas, estado (pagas/não pagas)
 *   - Selecção múltipla + eliminar em lote + eliminar todas
 *   - Reenviar credenciais por linha
 *   - Toggle PAGA/NÃO PAGA por linha
 *   - Painel "Preço + emails por idioma" (PT/EN/ES) com placeholders
 *   - Exportar CSV
 */
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Download, Search, X, ChevronDown, ChevronUp,
  Settings, Trash2, Send, Check, XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import InscricoesConfigModal from "./components/InscricoesConfigModal";

const ymd = (iso) => (iso || "").substring(0, 10);
const ymdhms = (iso) => (iso || "").substring(0, 19).replace("T", " ");
const stkOf = (n) => `STK-${Number(n) || 0}`;
const eur = (v, currency = "EUR") => {
  const num = Number(v || 0);
  if (currency === "EUR") return `${num.toFixed(2).replace(".", ",")} €`;
  return `${num.toFixed(2)} ${currency}`;
};

export default function AdminInscricoes() {
  const [inscriptions, setInscriptions] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [showConfig, setShowConfig] = useState(false);

  // filters
  const [q, setQ] = useState("");
  const [gameFilter, setGameFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const [inscRes, gamesRes] = await Promise.all([
        api.get("/inscriptions"),
        api.get("/games"),
      ]);
      setInscriptions(inscRes.data || []);
      setGames(gamesRes.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a carregar inscrições");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const gamesById = useMemo(() => {
    const m = {};
    for (const g of games) m[g.id] = g;
    return m;
  }, [games]);

  const filtered = useMemo(() => {
    let list = inscriptions;
    if (q.trim()) {
      const needle = q.toLowerCase();
      list = list.filter((i) =>
        (i.school || "").toLowerCase().includes(needle) ||
        (i.email || "").toLowerCase().includes(needle) ||
        stkOf(i.sequence_no).toLowerCase().includes(needle)
      );
    }
    if (gameFilter) list = list.filter((i) => i.game_id === gameFilter);
    if (dateFrom) list = list.filter((i) => ymd(i.created_at) >= dateFrom);
    if (dateTo) list = list.filter((i) => ymd(i.created_at) <= dateTo);
    if (statusFilter === "paid") list = list.filter((i) => !!i.paid);
    if (statusFilter === "unpaid") list = list.filter((i) => !i.paid);
    return [...list].sort((a, b) => (b.sequence_no || 0) - (a.sequence_no || 0));
  }, [inscriptions, q, gameFilter, dateFrom, dateTo, statusFilter]);

  const totalCreds = useMemo(
    () => filtered.reduce((sum, i) => sum + (i.credentials_ids?.length || 0), 0),
    [filtered]
  );
  const totalDue = useMemo(
    () => filtered.reduce((s, i) => s + (i.paid ? 0 : Number(i.total_amount || 0)), 0),
    [filtered]
  );

  const handleExportCsv = async () => {
    try {
      const r = await api.get("/inscriptions/export.csv", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "inscricoes.csv";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV exportado");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao exportar CSV");
    }
  };

  const togglePaid = async (insc) => {
    const wasPaid = !!insc.paid;
    try {
      await api.patch(`/inscriptions/${insc.id}`, {
        paid: !wasPaid,
        payment_method: !wasPaid ? "manual" : null,
      });
      toast.success(!wasPaid ? "Marcada como PAGA" : "Marcada como NÃO PAGA");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro a atualizar estado");
    }
  };

  const resendCreds = async (insc) => {
    try {
      const { data } = await api.post(`/inscriptions/${insc.id}/resend`);
      if (data?.ok) {
        toast.success(`Credenciais reenviadas para ${insc.email}`);
      } else {
        // Endpoint sync agora devolve o erro exato do backend (SMTP, SendGrid,
        // ou "not configured"). Mostrar o motivo real para o admin diagnosticar.
        toast.error(
          `Falha ao enviar para ${insc.email}: ${data?.email_error || "backend de email não configurado"}`,
          { duration: 12000 },
        );
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao reenviar");
    }
  };

  const deleteOne = async (insc) => {
    if (!window.confirm(
      `Eliminar ${stkOf(insc.sequence_no)} · ${insc.school}?\n\n` +
      `As ${insc.credentials_ids?.length || 0} credenciais associadas serão APAGADAS permanentemente.`
    )) return;
    try {
      const r = await api.delete(`/inscriptions/${insc.id}`);
      toast.success(
        `Inscrição eliminada · ${r.data?.deleted?.credentials || 0} credenciais apagadas`
      );
      setSelected((prev) => {
        const nx = new Set(prev);
        nx.delete(insc.id);
        return nx;
      });
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao eliminar");
    }
  };

  const deleteSelected = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    if (!window.confirm(
      `Eliminar ${ids.length} inscrição/inscrições selecionadas?\n\n` +
      `Todas as credenciais associadas serão APAGADAS permanentemente.`
    )) return;
    try {
      const r = await api.post("/inscriptions/bulk-delete", { ids });
      toast.success(
        `${r.data?.deleted?.inscriptions || 0} inscrições · ${r.data?.deleted?.credentials || 0} credenciais apagadas`
      );
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao eliminar em lote");
    }
  };

  const deleteAll = async () => {
    if (!inscriptions.length) return;
    if (!window.confirm(
      `⚠️ ELIMINAR TODAS as ${inscriptions.length} inscrições?\n\n` +
      `Todas as credenciais associadas serão APAGADAS permanentemente.\n` +
      `Esta ação NÃO pode ser desfeita.`
    )) return;
    try {
      const r = await api.post("/inscriptions/bulk-delete", { all: true });
      toast.success(
        `${r.data?.deleted?.inscriptions || 0} inscrições · ${r.data?.deleted?.credentials || 0} credenciais apagadas`
      );
      setSelected(new Set());
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao eliminar todas");
    }
  };

  const toggleSelect = (id) => {
    setSelected((prev) => {
      const nx = new Set(prev);
      if (nx.has(id)) nx.delete(id); else nx.add(id);
      return nx;
    });
  };

  const toggleSelectAllVisible = () => {
    const allVisible = filtered.every((i) => selected.has(i.id));
    setSelected((prev) => {
      const nx = new Set(prev);
      if (allVisible) filtered.forEach((i) => nx.delete(i.id));
      else filtered.forEach((i) => nx.add(i.id));
      return nx;
    });
  };

  const clearFilters = () => {
    setQ(""); setGameFilter(""); setDateFrom(""); setDateTo(""); setStatusFilter("");
  };

  const activeFilters = q || gameFilter || dateFrom || dateTo || statusFilter;
  const allVisibleSelected = filtered.length > 0 && filtered.every((i) => selected.has(i.id));

  return (
    <div className="p-6 max-w-7xl mx-auto text-slate-900" data-testid="admin-inscricoes">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-[Fredoka] font-bold text-slate-900">Inscrições de Finalistas</h1>
          <p className="text-sm text-slate-600 mt-1">
            {filtered.length} {filtered.length === 1 ? "inscrição" : "inscrições"} · {totalCreds} credenciais
            {totalDue > 0 ? <> · <span className="text-orange-600 font-semibold">{eur(totalDue)}</span> por receber</> : null}
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setShowConfig(true)} className="rounded-full" data-testid="btn-config">
            <Settings className="w-4 h-4 mr-1.5" /> Preço & emails
          </Button>
          <Button variant="outline" onClick={load} disabled={loading} className="rounded-full" data-testid="btn-refresh">
            <RefreshCw className={`w-4 h-4 mr-1.5 ${loading ? "animate-spin" : ""}`} /> Atualizar
          </Button>
          <Button onClick={handleExportCsv} disabled={loading || !filtered.length} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="btn-export-csv">
            <Download className="w-4 h-4 mr-1.5" /> Exportar CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 mb-3 grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="md:col-span-2">
          <Label className="text-xs">Procurar por STK, escola ou email</Label>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ex: STK-42 ou Escola de Lisboa" className="pl-8" data-testid="inscricoes-search" />
          </div>
        </div>
        <div>
          <Label className="text-xs">Jogo</Label>
          <select value={gameFilter} onChange={(e) => setGameFilter(e.target.value)}
            className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="inscricoes-game-filter">
            <option value="">— todos —</option>
            {games.filter((g) => g.status === "competition").map((g) => <option key={g.id} value={g.id}>{g.name || g.id}</option>)}
          </select>
        </div>
        <div>
          <Label className="text-xs">Estado</Label>
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="w-full h-10 rounded-md border border-slate-200 px-2 text-sm bg-white" data-testid="inscricoes-status-filter">
            <option value="">— todos —</option>
            <option value="paid">Pagas</option>
            <option value="unpaid">Não pagas</option>
          </select>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-xs">De</Label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid="inscricoes-date-from" />
          </div>
          <div>
            <Label className="text-xs">Até</Label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid="inscricoes-date-to" />
          </div>
        </div>
        {activeFilters && (
          <div className="md:col-span-5">
            <Button variant="ghost" onClick={clearFilters} className="text-rose-600 hover:text-rose-700 h-8 text-xs" data-testid="btn-clear-filters">
              <X className="w-3.5 h-3.5 mr-1" /> Limpar filtros
            </Button>
          </div>
        )}
      </div>

      {/* Bulk actions bar */}
      {(selected.size > 0 || inscriptions.length > 0) && (
        <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
          <div className="text-xs text-slate-600">
            {selected.size > 0 ? (
              <span className="font-semibold text-slate-800">{selected.size} selecionada{selected.size !== 1 ? "s" : ""}</span>
            ) : (
              <span>Sem seleção</span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={deleteSelected} disabled={!selected.size}
              className="text-rose-600 border-rose-200 hover:bg-rose-50" data-testid="btn-delete-selected">
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar selecionadas
            </Button>
            <Button variant="outline" size="sm" onClick={deleteAll} disabled={!inscriptions.length}
              className="text-rose-700 border-rose-300 hover:bg-rose-100" data-testid="btn-delete-all">
              <Trash2 className="w-4 h-4 mr-1" /> Eliminar todas
            </Button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto shadow-sm">
        <table className="w-full text-sm min-w-[1050px]">
          <thead className="bg-slate-100 border-b border-slate-200 text-xs uppercase text-slate-600">
            <tr>
              <th className="text-left px-2 py-2 w-8">
                <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAllVisible}
                  aria-label="Selecionar tudo" data-testid="check-all" />
              </th>
              <th className="text-left px-2 py-2 w-6"></th>
              <th className="text-left px-3 py-2">STK</th>
              <th className="text-left px-3 py-2">Data</th>
              <th className="text-left px-3 py-2">Escola</th>
              <th className="text-left px-3 py-2">Email</th>
              <th className="text-left px-3 py-2">Jogo</th>
              <th className="text-right px-3 py-2">Cred.</th>
              <th className="text-right px-3 py-2">Total</th>
              <th className="text-center px-3 py-2">Estado</th>
              <th className="text-right px-3 py-2 w-40">Ações</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={11} className="text-center py-6 text-slate-500">A carregar…</td></tr>
            )}
            {!loading && filtered.length === 0 && (
              <tr><td colSpan={11} className="text-center py-8 text-slate-500" data-testid="empty-state">
                {inscriptions.length === 0 ? "Ainda não há inscrições." : "Nenhuma inscrição corresponde aos filtros."}
              </td></tr>
            )}
            {filtered.map((insc) => {
              const game = gamesById[insc.game_id];
              const gameName = game?.name || insc.game_id;
              const isOpen = expandedId === insc.id;
              const isSelected = selected.has(insc.id);
              return (
                <React.Fragment key={insc.id}>
                  <tr className={`border-b border-slate-100 hover:bg-slate-50 ${isSelected ? "bg-blue-50/40" : ""}`}
                    data-testid={`inscricao-row-${insc.id}`}>
                    <td className="px-2 py-2">
                      <input type="checkbox" checked={isSelected} onChange={() => toggleSelect(insc.id)}
                        data-testid={`check-${insc.id}`} />
                    </td>
                    <td className="px-2 py-2">
                      <button type="button" onClick={() => setExpandedId(isOpen ? null : insc.id)}
                        className="w-6 h-6 grid place-items-center rounded hover:bg-slate-200"
                        data-testid={`toggle-${insc.id}`}>
                        {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </button>
                    </td>
                    <td className="px-3 py-2 font-mono text-xs font-bold text-slate-800" data-testid={`stk-${insc.id}`}>
                      {stkOf(insc.sequence_no)}
                    </td>
                    <td className="px-3 py-2 whitespace-nowrap font-mono text-[11px] text-slate-600">{ymdhms(insc.created_at)}</td>
                    <td className="px-3 py-2 font-semibold">{insc.school}</td>
                    <td className="px-3 py-2 text-slate-600">{insc.email}</td>
                    <td className="px-3 py-2">{gameName}</td>
                    <td className="px-3 py-2 text-right font-mono">{insc.credentials_ids?.length ?? 0}</td>
                    <td className="px-3 py-2 text-right font-mono">{eur(insc.total_amount, insc.currency)}</td>
                    <td className="px-3 py-2 text-center">
                      <button type="button" onClick={() => togglePaid(insc)}
                        data-testid={`toggle-paid-${insc.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider border ${
                          insc.paid
                            ? "bg-emerald-100 text-emerald-800 border-emerald-300 hover:bg-emerald-200"
                            : "bg-orange-100 text-orange-800 border-orange-300 hover:bg-orange-200"
                        }`}>
                        {insc.paid ? <><Check className="w-3 h-3" /> Paga</> : <><XCircle className="w-3 h-3" /> Não paga</>}
                      </button>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex items-center justify-end gap-1">
                        <button type="button" onClick={() => resendCreds(insc)}
                          className="w-7 h-7 grid place-items-center rounded-md hover:bg-blue-100 text-blue-600"
                          title="Reenviar credenciais" data-testid={`btn-resend-${insc.id}`}>
                          <Send className="w-4 h-4" />
                        </button>
                        <button type="button" onClick={() => deleteOne(insc)}
                          className="w-7 h-7 grid place-items-center rounded-md hover:bg-rose-100 text-rose-600"
                          title="Eliminar inscrição" data-testid={`btn-delete-${insc.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <td colSpan={11} className="px-6 py-3">
                        <div className="text-xs">
                          <div className="font-bold text-slate-700 mb-1 uppercase">Níveis/Tiers desta inscrição</div>
                          <ul className="space-y-1">
                            {(insc.levels || []).map((lvl, idx) => {
                              const tier = (game?.tiers || []).find((t) => t.id === lvl.tier_id);
                              const label = tier?.name || `Nível ${lvl.level}`;
                              return (
                                <li key={idx} className="flex justify-between border-b border-slate-100 py-0.5">
                                  <span>{label}</span>
                                  <span className="font-mono">{lvl.quantity} aluno{lvl.quantity !== 1 ? "s" : ""}</span>
                                </li>
                              );
                            })}
                          </ul>
                          {insc.credentials_ids?.length ? (
                            <div className="mt-3 text-slate-500">
                              <span className="font-bold text-slate-700">IDs das credenciais:</span>{" "}
                              <span className="font-mono break-all">{insc.credentials_ids.join(", ")}</span>
                            </div>
                          ) : null}
                          {insc.paid ? (
                            <div className="mt-2 text-emerald-700">
                              Paga em {ymdhms(insc.paid_at) || "—"} · Método: <strong>{insc.payment_method || "manual"}</strong>
                              {insc.payment_ref ? <> · Ref: <span className="font-mono">{insc.payment_ref}</span></> : null}
                            </div>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {showConfig && (
        <InscricoesConfigModal onClose={() => setShowConfig(false)} onSaved={load} />
      )}
    </div>
  );
}
